import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {InitializationAndConfigurationService} from './configuration/initializationAndConfiguration.service';
import {BrowserDetect} from './support/browser-detect.service';
import {UrlService} from './url.service';

@Injectable()
export class AdminService {
    private BASE_URL: string;
    private tags = null;
    private ous = null;

    constructor(private http: HttpClient,
                private initializationAndConfigurationService: InitializationAndConfigurationService,
                private browserDetect: BrowserDetect,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/admin';
    }

    findResponsibleOus(ous): any[] {
        const ret = [];
        ous.forEach((ou) => {
            if (this.initializationAndConfigurationService.userIsOuResponsible(ou)) {
                ret.push(ou);
            } else if (ou != null) {
                if (ou.children != null && ou.children.length > 0) {
                    const childRes = this.findResponsibleOus(ou.children);
                    if (childRes.length > 0) {
                        childRes.forEach((el) => {
                            ret.push(el)
                        });
                    }
                }
            }
        });
        return ret;
    }

    filterTags(types, tags) {
        if (types) {
            const res = [];
            tags.forEach((tagRoot) => {
                let include = false;
                types.forEach((ruType) => {
                    if (tagRoot[ruType]) {
                        include = true;
                    }
                });
                if (include) {
                    res.push(tagRoot);
                }
            });
            return res;
        } else {
            return tags;
        }
    }

    keepAlive() {
        // The /admin/keepAlive endpoint declares Produces=application/json but returns
        // the literal text "Ok." (not valid JSON), which makes HttpClient log a parse
        // error on every 200 OK response. Tell it to expect text so the response is
        // delivered as-is.
        this.http.get(this.BASE_URL + '/keepAlive', { responseType: 'text' }).subscribe({
            next: () => {} // Do Nothing
        });
    }

    getTags(types) {
        if (this.tags) {
            return new Observable<any>(obs => {
                obs.next(this.filterTags(types, this.tags));
                obs.complete();
            });
        } else {
            return this.http.get(this.BASE_URL + '/tags').pipe(map(tags => {
                this.tags = tags;
                return this.filterTags(types, tags);
            }));
        }
    }

    getOus() {
        if (this.ous) {
            return new Observable<any>(obs => {
                obs.next(this.findResponsibleOus(this.ous));
                obs.complete();
            });
        } else {
            return this.http.get(this.BASE_URL + '/ous').pipe(
                map((ous) => {
                    this.ous = ous;
                    return this.findResponsibleOus(ous);
                })
            );
        }
    }

    encodeReportURLParams(params: string): Observable<any> {
        let httpParams = new HttpParams();
        if (params) {
            httpParams = httpParams.set('params', params);
        }

        return this.http.get(this.BASE_URL + '/encodeParams', {
            params: httpParams,
            responseType: 'text'
        });
    }

    encodeReportUrl(url: string, unencodedParams: string) {
        const idx = url.indexOf('&') + 1;
        const params = url.substring(idx);
        const baseUrl = url.substring(0, idx);

        this.encodeReportURLParams(params).subscribe({
            next: (res: any) => {
                // The response is a string like "params=encryptedValue"
                // We want to use the full response string as-is
                const finalUrl = baseUrl + res + unencodedParams;

                // Open the popup
                this.browserDetect.downloadFile(finalUrl);
            },
            error: (error) => {
                console.error('Error encoding report URL parameters:', error);
                // Handle error appropriately - maybe show a user-friendly message
            }
        });
    }

    getFlatOus(ouIds: string): Observable<any> {
        let params = new HttpParams();
        if (ouIds) {
            params = params.set('ouIds', ouIds);
        }

        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/flatOus', requestOptions);
    }

    getFlatTags(tagIds: string): Observable<any> {
        let params = new HttpParams();
        if (tagIds) {
            params = params.set('tagIds', tagIds);
        }

        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/flatTags', requestOptions);
    }

    listDiagnoses(params: any): Observable<any> {
        let httpParams = new HttpParams();

        // Convert the params object to HttpParams
        if (params) {
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    httpParams = httpParams.set(key, params[key].toString());
                }
            });
        }

        const requestOptions = {
            params: httpParams
        };

        return this.http.get(this.urlService.getBackendUrl() + 'data/diagnoses/all', requestOptions);
    }

    getConfiguration(): Observable<any> {
        return this.initializationAndConfigurationService.getConfiguration();
    }
}
